import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MultiSelectFilter from '../../../components/consguard/MultiSelectFilter';
import { getSeverityStars } from '../../../utils/consguard/severity';

interface DashboardTableProps {
    data: any[];
}

type SortField = 'no' | 'title' | 'category' | 'severity' | 'reviewer' | 'status' | 'detected' | null;
type SortOrder = 'asc' | 'desc';

const getStatusClass = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'open': return 'open';
    case 'in review':
    case 'in_review':
    case 'review': return 'in-review'; 
    case 'resolved':
    case 'approved': return 'resolved';
    case 'archived': return 'archived';
    default: return '';
  }
};

const DashboardTable: React.FC<DashboardTableProps> = ({ data }) => {
  const [sortField, setSortField] = useState<SortField>('no');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    category: [] as string[],
    status: [] as string[],
    reviewer: [] as string[]
  });

  useEffect(() => {
    setActiveFilters({ category: [], status: [], reviewer: [] });
  }, [data]);

  const FILTER_DEFS = [
    { key: 'category', label: 'Category', distinctKey: 'Categories' },
    { key: 'status', label: 'Status', distinctKey: 'Statuses' },
    { key: 'reviewer', label: 'Reviewer', distinctKey: 'Reviewers' },
  ] as const;

  const distinct = useMemo(() => ({
    Categories: Array.from(new Set(data.map(d => d.category))).filter(Boolean),
    Statuses: Array.from(new Set(data.map(d => d.status))).filter(Boolean),
    Reviewers: Array.from(new Set(data.map(d => d.reviewer))).filter(Boolean),
  }), [data]);

  const filteredData = useMemo(() => {
    return data.filter(row => {
      const matchSearch = (row.title?.toLowerCase().includes(search.toLowerCase()) || false) || (row.inconsistency_number?.toLowerCase().includes(search.toLowerCase()) || false);
      const matchCategory = activeFilters.category.length > 0 ? activeFilters.category.includes(row.category) : true;
      const matchStatus = activeFilters.status.length > 0 ? activeFilters.status.includes(row.status) : true;
      const matchReviewer = activeFilters.reviewer.length > 0 ? activeFilters.reviewer.includes(row.reviewer) : true;
      return matchSearch && matchCategory && matchStatus && matchReviewer;
    });
  }, [data, search, activeFilters]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedData = useMemo(() => {
    const result = [...filteredData];
    if (sortField) {
      result.sort((a, b) => {
        if (sortField === 'no') {
          const strA = (a.inconsistency_number || '').toString();
          const strB = (b.inconsistency_number || '').toString();
          const comp = strA.localeCompare(strB, undefined, { numeric: true, sensitivity: 'base' });
          return sortOrder === 'asc' ? comp : -comp;
        }

        let valA = a[sortField];
        let valB = b[sortField];

        if (sortField === 'severity') {
          const severityOrder: Record<string, number> = { HIGH: 3, MEDIUM: 2, LOW: 1 };
          const sA = (a.severity || '').toString().toUpperCase();
          const sB = (b.severity || '').toString().toUpperCase();
          valA = severityOrder[sA] || sA.length;
          valB = severityOrder[sB] || sB.length;
        } else if (typeof valA === 'string' && typeof valB === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }

        if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [filteredData, sortField, sortOrder]);

  const handleRowClick = (row: any) => {
    const params = new URLSearchParams(window.location.search);
    if (row.project_id || row.projectId) {
      params.set('project', row.project_id || row.projectId);
    }
    if (row.inconsistency_number) {
      params.set('inconsistency_number', row.inconsistency_number);
    }
    navigate(`/consguard?${params.toString()}`);
  };

  const SortArrow = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="cg-sort-arrow">▲</span>;
    return <span className="cg-sort-arrow">{sortOrder === 'asc' ? '▲' : '▼'}</span>;
  };

  return (
    <div className="cg-dashboard-table-wrapper">
      <div className="cg-table-controls">
        <div className="cg-search-wrapper">
          <svg className="cg-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          <input
            type="text"
            className="cg-search-input"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="cg-dashboard-filters">
          {FILTER_DEFS.map(({ key, label, distinctKey }) => (
            <MultiSelectFilter
              key={key}
              label={label}
              options={distinct[distinctKey] as string[]}
              checkedValues={activeFilters[key as keyof typeof activeFilters]}
              onChange={(checked) =>
                setActiveFilters(prev => ({ ...prev, [key]: checked }))
              }
            />
          ))}
        </div>
      </div>
      <table className="cg-dashboard-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('no')} style={{ width: '12%' }}>ID <SortArrow field="no" /></th>
            <th onClick={() => handleSort('title')} style={{ width: '28%' }}>Title <SortArrow field="title" /></th>
            <th onClick={() => handleSort('category')} style={{ width: '15%' }}>Category <SortArrow field="category" /></th>
            <th onClick={() => handleSort('severity')} style={{ width: '10%' }}>Severity <SortArrow field="severity" /></th>
            <th onClick={() => handleSort('reviewer')} style={{ width: '15%' }}>Reviewer <SortArrow field="reviewer" /></th>
            <th onClick={() => handleSort('status')} style={{ width: '10%' }}>Status <SortArrow field="status" /></th>
            <th onClick={() => handleSort('detected')} style={{ width: '10%' }}>Detected <SortArrow field="detected" /></th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row) => (
            <tr key={row.real_inconsistency_id} onClick={() => handleRowClick(row)} style={{ cursor: 'pointer' }}>
              <td className="cg-td-no" title={row.inconsistency_number || row.real_inconsistency_id}>
                {row.inconsistency_number}
              </td>
              <td className="cg-td-title">
                <div className="cg-title-content">
                  {row.title}
                  {row.isNew && <span className="cg-new-badge">*New</span>}
                </div>
              </td>
              <td title={row.category} className="cg-truncate">{row.category}</td>
              <td style={{ color: '#000', letterSpacing: '1px', fontSize: '14px', fontWeight: 600 }}>{getSeverityStars(row.severity)}</td>
              <td>
                <div className="cg-reviewer-cell" onClick={(e) => e.stopPropagation()}>
                  {row.reviewer || ''}
                </div>
              </td>
              <td>
                <span className={`cg-status ${getStatusClass(row.status)}`}>
                  {row.status}
                </span>
              </td>
              <td className="cg-td-detected">{row.detected}</td>
            </tr>
          ))}
          {sortedData.length === 0 && (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                No inconsistencies found matching the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DashboardTable;
